"use client";

import React, { useCallback, useMemo } from "react";
import {
  PayPalButtons,
  usePayPalScriptReducer,
  FUNDING,
  PayPalButtonsComponentProps,
} from "@paypal/react-paypal-js";
import type {
  CreateOrderData,
  CreateOrderActions,
  OnApproveData,
  OnApproveActions,
} from "@paypal/paypal-js";
import { cn } from "@/lib/utils";
import { usePayPalCheckout } from "@/hooks/usePaypalCheckout";
import { usePaypalContext } from "@/components/checkout/paypal-provider";
import { useIsMutating } from "@tanstack/react-query";
import { PayPalButtonsWithResume } from "../paypal-buttons-with-resume";
import { shouldUsePaypalVaultFlow } from "@/components/checkout/paypal-vault-flow";
import { FLUID_CDN_URL } from "@/lib/constants";
import { useApplePayPalCheckout } from "@/hooks/usePPCPApplePay";

interface UsePaypalProps {
  fastCheckout?: boolean;
  cartToken?: string;
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  disabled?: boolean;
  deviceData?: string;
  kountSessionId?: string;
  // Start-free / vault-without-charge carts: render the vault button options
  // (createVaultSetupToken) instead of the charge createOrder/onApprove.
  vaultWithoutCharge?: boolean;
}

interface PayPalButtonRendererProps extends UsePaypalProps {
  fundingSource:
    | typeof FUNDING.PAYPAL
    | typeof FUNDING.VENMO
    | typeof FUNDING.PAYLATER;
  isVenmo?: boolean;
  isPayLater?: boolean;
  className?: string;
}

const PayPalButtonSkeleton = ({ className }: { className?: string }) => {
  return (
    <div className={cn("h-input w-full overflow-hidden rounded", className)}>
      <div className="h-full w-full animate-pulse rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
    </div>
  );
};

type PayPalButtonsOptions = React.ComponentProps<typeof PayPalButtons>;

const PayPalButtonRenderer = ({
  className,
  fundingSource,
  isVenmo = false,
  isPayLater = false,
  cartToken,
  onSuccess,
  onError,
  onRedirect,
  paymentMethodsIncludes,
  fastCheckout,
  disabled,
  vaultWithoutCharge,
}: PayPalButtonRendererProps) => {
  const [{ isPending, isResolved }] = usePayPalScriptReducer();

  const {
    isProcessing,
    createOrder,
    onApprove,
    createVaultSetupToken,
    onApproveVault,
    onPayPalError,
    onShippingAddressChange,
    onShippingOptionsChange,
  } = usePayPalCheckout({
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    fastCheckout,
  });

  const isVaultFlow = shouldUsePaypalVaultFlow(
    { vault_without_charge: vaultWithoutCharge },
    fundingSource,
  );

  const isSubscriptionPending =
    useIsMutating({ mutationKey: ["update-item-subscription", cartToken] }) > 0;
  const isQuantityPending =
    useIsMutating({ mutationKey: ["update-item-quantity", cartToken] }) > 0;
  const isDiscountPending =
    useIsMutating({ mutationKey: ["apply-discount", cartToken] }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({ mutationKey: ["remove-discount", cartToken] }) > 0;

  const isCartMutating =
    isSubscriptionPending ||
    isQuantityPending ||
    isDiscountPending ||
    isRemoveDiscountPending;

  const buttonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      ...(fastCheckout && { color: "gold", tagline: false }),
    }),
    [fastCheckout],
  );

  const venmoButtonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      color: "blue",
      tagline: false,
    }),
    [],
  );

  const payLaterButtonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      color: "white",
      tagline: false,
    }),
    [],
  );

  const getButtonStyle =
    useCallback((): PayPalButtonsComponentProps["style"] => {
      if (isPayLater) return payLaterButtonStyles;
      if (isVenmo) return venmoButtonStyles;
      return buttonStyles;
    }, [
      isPayLater,
      isVenmo,
      buttonStyles,
      venmoButtonStyles,
      payLaterButtonStyles,
    ]);

  const memoOptions = useMemo<PayPalButtonsOptions>(() => {
    const sharedOptions: PayPalButtonsOptions = {
      fundingSource,
      style: getButtonStyle(),
      className,
      disabled: isProcessing || !cartToken || isCartMutating || disabled,

      onError: (error: Record<string, unknown>) => {
        console.error("PayPal SDK error:", error);
        try {
          onPayPalError(error);
        } catch (e) {
          console.error("Error in onPayPalError handler:", e);
        }
      },

      onCancel: () => {},
    };

    // A PayPalButtons instance must carry EITHER createVaultSetupToken OR
    // createOrder, never both. The vault flow has no resumable order, so it
    // skips the order-scoped shipping-change callbacks too.
    if (isVaultFlow) {
      return {
        ...sharedOptions,

        createVaultSetupToken: () => {
          if (!cartToken) {
            return Promise.reject(new Error("Missing cart token"));
          }
          return createVaultSetupToken(cartToken);
        },

        onApprove: (_data: OnApproveData, _actions: OnApproveActions) => {
          if (!cartToken) {
            return Promise.resolve();
          }
          return onApproveVault(cartToken);
        },
      };
    }

    return {
      ...sharedOptions,

      createOrder: (data: CreateOrderData, actions: CreateOrderActions) => {
        if (!cartToken) {
          return Promise.reject(new Error("Missing cart token"));
        }
        return createOrder(data, actions, cartToken);
      },

      onApprove: (data: OnApproveData, actions: OnApproveActions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onApprove(data, actions, cartToken);
      },

      onShippingAddressChange: (data, actions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onShippingAddressChange(data, actions, cartToken);
      },

      onShippingOptionsChange: (data, actions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onShippingOptionsChange(data, actions, cartToken);
      },
    };
  }, [
    fundingSource,
    getButtonStyle,
    className,
    isProcessing,
    cartToken,
    isCartMutating,
    disabled,
    isVaultFlow,
    createOrder,
    onApprove,
    createVaultSetupToken,
    onApproveVault,
    onPayPalError,
    onShippingAddressChange,
    onShippingOptionsChange,
  ]);

  if (isPending || !isResolved || isProcessing) {
    return <PayPalButtonSkeleton className={className} />;
  }

  if (!cartToken) {
    return null;
  }

  // The checkout handlers (charge createOrder/onApprove and the vault
  // createVaultSetupToken/onApproveVault) are always returned by
  // usePayPalCheckout, so no presence guard is needed here.
  return (
    <PayPalButtonsWithResume options={memoOptions} isVaultFlow={isVaultFlow} />
  );
};

PayPalButtonRenderer.displayName = "PayPalButtonRenderer";

// ========== APPLE PAY BUTTON RENDERER (using PayPal) ==========
// Note: Eligibility is already checked by PaypalProvider, this component just needs the config
const ApplePayButtonRenderer = React.memo<{
  fastCheckout?: boolean;
  cartToken?: string;
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  className?: string;
  disabled?: boolean;
  deviceData?: string;
  kountSessionId?: string;
}>(
  ({
    className,
    cartToken,
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    disabled,
    deviceData,
    kountSessionId,
  }) => {
    const [{ isPending: isScriptLoading }] = usePayPalScriptReducer();
    // Apple Pay config is fetched once and settled by PaypalProvider (de-raced),
    // then passed via context — the renderer no longer fetches it itself.
    const { applePayConfig: config } = usePaypalContext();

    const { isProcessing, handleApplePayPalCheckout } = useApplePayPalCheckout({
      onSuccess,
      onError,
      onRedirect,
      paymentMethodsIncludes,
      deviceData,
      kountSessionId,
    });

    const handleApplePayClick = () => {
      if (!cartToken || !config) return;
      handleApplePayPalCheckout(cartToken, config);
    };

    // Show skeleton while the SDK script loads.
    if (isScriptLoading || isProcessing) {
      return <PayPalButtonSkeleton className={className} />;
    }

    // Don't show if config is unavailable (settled ineligible / failed).
    if (!config) return null;

    return (
      <button
        type="button"
        data-testid="apple-pay-ppcp-button"
        className={cn(
          "h-input flex w-full items-center justify-center rounded bg-black py-2 text-white transition-colors duration-200 hover:bg-gray-600",
          className,
        )}
        onClick={handleApplePayClick}
        disabled={!cartToken || isProcessing || disabled}
      >
        <img
          src={`${FLUID_CDN_URL}/images/checkout/icons/ApplePayWhite.png`}
          alt="Apple Pay"
          width={50}
          height={50}
        />
      </button>
    );
  },
);
ApplePayButtonRenderer.displayName = "ApplePayButtonRenderer";

// ========== MAIN HOOK ==========
export default function usePaypal(props: UsePaypalProps) {
  const {
    fastCheckout,
    cartToken,
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    disabled,
    deviceData,
    kountSessionId,
    vaultWithoutCharge,
  } = props;

  const {
    shouldShowVenmo,
    shouldShowPayLater,
    shouldShowApplePay,
    isProviderReady,
    isRejected,
  } = usePaypalContext();

  if (!isProviderReady || isRejected) {
    return {
      paypalButton: null,
      venmoButton: null,
      payLaterButton: null,
      applePayButton: null,
    };
  }

  return {
    // Base PayPal is the always-eligible core funding source: it mounts on
    // isResolved (not gated on funding settle) so a slow/failed settle never
    // suppresses the one button that always works. Venmo/PayLater/ApplePay
    // below gate on the settled context flags.
    paypalButton: (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        vaultWithoutCharge={vaultWithoutCharge}
        fundingSource={FUNDING.PAYPAL}
        className={cn(!fastCheckout && "mt-6 mb-[-6px]")}
      />
    ),
    venmoButton: shouldShowVenmo ? (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        vaultWithoutCharge={vaultWithoutCharge}
        fundingSource={FUNDING.VENMO}
        isVenmo
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    payLaterButton: shouldShowPayLater ? (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        fundingSource={FUNDING.PAYLATER}
        isPayLater
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    applePayButton: shouldShowApplePay ? (
      <ApplePayButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        deviceData={deviceData}
        kountSessionId={kountSessionId}
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
  };
}
